import { Box, Stack } from "@mui/material";
import { type ReactNode } from "react";

import { Text } from "../../components/Text";
import { Title } from "../../components/Title";
import { getBorderBase, getStrongText } from "../themeHelpers";

interface HelpCenterHeroProps {
  children?: ReactNode;
  isMobile: boolean;
}

export function HelpCenterHero(props: HelpCenterHeroProps) {
  const { children, isMobile } = props;

  return (
    <Box
      sx={(theme) => ({
        borderRadius: theme.borderRadius?.xLarge,
        backgroundColor: theme.palette.background.baseDark,
        border: `1px solid ${getBorderBase(theme)}`,
        boxShadow: theme.shadow?.card,
        paddingTop: theme.spacing(isMobile ? 3 : 7),
        paddingRight: theme.spacing(isMobile ? 3 : 6),
        paddingBottom: theme.spacing(isMobile ? 7 : 7),
        paddingLeft: theme.spacing(isMobile ? 3 : 6),
      })}
    >
      <Stack
        sx={{
          alignItems: "center",
          gap: isMobile ? "1rem" : "0.5rem",
          maxWidth: "32rem",
          width: "100%",
          marginX: "auto",
        }}
      >
        <Box
          sx={(theme) => ({
            display: "inline-flex",
            alignItems: "center",
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(2),
            borderRadius: theme.borderRadius?.xxxLarge,
            backgroundColor: theme.palette.primary.main,
            border: `1px solid ${getBorderBase(theme)}`,
          })}
        >
          <Text
            semibold
            variant="caption"
            sx={(theme) => ({ color: theme.palette.base?.inverted?.strong ?? "#FFFFFF" })}
          >
            Help Center
          </Text>
        </Box>

        <Title
          bold
          component="h5"
          variant="h5"
          sx={(theme) => ({
            color: getStrongText(theme),
            fontSize: isMobile ? "1.95rem" : "3rem",
            lineHeight: 1.1,
            letterSpacing: "-0.03em",
            textAlign: "center",
          })}
        >
          How can we help?
        </Title>

        <Text
          variant="body2"
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            textAlign: "center",
            fontSize: isMobile ? theme.typography.body1.fontSize : theme.typography.h6.fontSize,
          })}
        >
          Get answers about managing shifts, coordinating workers, and troubleshooting your account.
        </Text>

        {children !== undefined && (
          <Box
            sx={(theme) => ({
              width: "100%",
              paddingTop: theme.spacing(isMobile ? 1 : 6),
            })}
          >
            {children}
          </Box>
        )}
      </Stack>
    </Box>
  );
}
